import { Button } from "@/components/elements/Button";
import { Select } from "@/components/elements/Select";
import { TalentSelectDropdown, TalentOption, TalentSelectDropdownRef } from "@/components/elements/TalentSelectDropdown/TalentSelectDropdown";
import { IconBox } from "@/components/elements/IconBox";
import { ConfirmSaveModal } from "@/components/commons/ConfirmSaveModal";
import { IconClose, IconSave } from "@/assets/icons";
import { background, black, divider, text } from "@/theme/colors";
import { Box, Modal as MuiModal, Stack, Typography, styled } from "@mui/material";
import { FC, useCallback, useMemo, useRef, useState } from "react";
import { TCaseMatches } from "@/api/services/main/case/dtos/get-case.dto";
import { CaseMatchStatusOptions, ECaseMatchStatus } from "@/types/enum";
import { checkMissingRequiredFields } from "@/features/case/utils/checkMissingRequiredFields";
import { isExemptSpecifiedSkillCert } from "@/features/case/utils/is-exempt-specified-skill-cert";

export type TBulkStatusChange = { id: number; status: number };

type TBulkChangeTalentStatusModalProps = {
  open: boolean;
  onClose: () => void;
  rows: TCaseMatches[];
  /** Rows passing required-field validation; parent persists to backend. */
  onSaveApi: (changes: TBulkStatusChange[]) => Promise<void>;
  /** Rows failing required-field validation; parent applies change locally without persisting. */
  onSaveUiOnly: (changes: TBulkStatusChange[]) => void;
  jobResidenceStatusIds?: number[];
};

type TSection = {
  status: number | null;
  talentIds: number[];
};

const INITIAL_SECTION: TSection = { status: null, talentIds: [] };

// CANDIDATE_WITHDRAWN requires a follow-up talent-status prompt (SelectTalentStatusModal), which the bulk flow does not implement — exclude it here.
const statusOptions = CaseMatchStatusOptions.filter((opt) => opt.value !== ECaseMatchStatus.CANDIDATE_WITHDRAWN).map((opt) => ({
  label: opt.label,
  value: opt.value,
}));

export const BulkChangeTalentStatusModal: FC<TBulkChangeTalentStatusModalProps> = ({
  open,
  onClose,
  rows,
  onSaveApi,
  onSaveUiOnly,
  jobResidenceStatusIds = [],
}) => {
  const [section1, setSection1] = useState<TSection>(INITIAL_SECTION);
  const [section2, setSection2] = useState<TSection>(INITIAL_SECTION);
  const [isSaving, setIsSaving] = useState(false);

  // Track pending (uncommitted) talent picks inside each dropdown. Parent needs visibility so 保存 can warn before losing data.
  const [section1HasUnsaved, setSection1HasUnsaved] = useState(false);
  const [section2HasUnsaved, setSection2HasUnsaved] = useState(false);
  const [isUnsavedConfirmOpen, setIsUnsavedConfirmOpen] = useState(false);

  // Imperative refs let us flush pending picks from each dropdown before running the save pipeline.
  const dropdown1Ref = useRef<TalentSelectDropdownRef | null>(null);
  const dropdown2Ref = useRef<TalentSelectDropdownRef | null>(null);

  const allTalentOptions = useMemo<TalentOption[]>(
    () =>
      rows
        .filter((row) => row.talent)
        .map((row) => ({
          id: row.talent.id,
          fullName: row.talent.fullName ?? row.talent.nickName ?? "—",
          nickname: row.talent.nickName,
        })),
    [rows],
  );

  const talentOptionsForSection = (excludeIds: number[]): TalentOption[] => {
    if (excludeIds.length === 0) return allTalentOptions;
    const exclude = new Set(excludeIds);
    return allTalentOptions.filter((t) => !exclude.has(t.id));
  };

  const statusOptionsForSection = (excludeStatus: number | null) => {
    if (excludeStatus === null) return statusOptions;
    return statusOptions.filter((opt) => opt.value !== excludeStatus);
  };

  const talentToCaseMatchId = useMemo(() => {
    const map = new Map<number, number>();
    rows.forEach((row) => {
      if (row.talent?.id) map.set(row.talent.id, row.id);
    });
    return map;
  }, [rows]);

  const canSave =
    (section1.status !== null && section1.talentIds.length > 0) || (section2.status !== null && section2.talentIds.length > 0);

  const reset = () => {
    setSection1(INITIAL_SECTION);
    setSection2(INITIAL_SECTION);
    setSection1HasUnsaved(false);
    setSection2HasUnsaved(false);
  };

  // Build bulk payload from the current `section1`/`section2` committed state and dispatch it to parent save handlers.
  const performSave = useCallback(
    async (s1: TSection, s2: TSection) => {
      const perCaseMatch = new Map<number, number>();
      [s1, s2].forEach((section) => {
        if (section.status === null) return;
        section.talentIds.forEach((talentId) => {
          const caseMatchId = talentToCaseMatchId.get(talentId);
          if (caseMatchId !== undefined) perCaseMatch.set(caseMatchId, section.status!);
        });
      });

      // Split: rows passing field validation → API save, rows failing → UI-only (mirrors per-row status-change behavior)
      const apiChanges: TBulkStatusChange[] = [];
      const uiOnlyChanges: TBulkStatusChange[] = [];
      perCaseMatch.forEach((status, id) => {
        const row = rows.find((r) => r.id === id);
        if (!row) return;

        const isExempt = isExemptSpecifiedSkillCert(jobResidenceStatusIds, row.talent?.residenceStatus?.residenceStatus);

        const hasMissing = checkMissingRequiredFields(row, status, isExempt);
        (hasMissing ? uiOnlyChanges : apiChanges).push({ id, status });
      });

      setIsSaving(true);
      try {
        if (uiOnlyChanges.length > 0) {
          onSaveUiOnly(uiOnlyChanges);
        }
        if (apiChanges.length > 0) await onSaveApi(apiChanges);
        reset();
        onClose();
      } finally {
        setIsSaving(false);
      }
    },
    [onClose, onSaveApi, onSaveUiOnly, rows, talentToCaseMatchId, jobResidenceStatusIds],
  );

  const handleSaveClick = () => {
    // If any dropdown has picks that were not 設定-committed, warn before saving so the user can decide whether to discard them.
    if (section1HasUnsaved || section2HasUnsaved) {
      setIsUnsavedConfirmOpen(true);
      return;
    }
    void performSave(section1, section2);
  };

  const handleConfirmUnsavedSave = () => {
    // Flush pending picks from each dropdown. `commitPending` invokes our `onChange` → `setSection(...)`,
    // so the next render has the committed ids. We capture those ids synchronously by reading each dropdown's
    // latest `onChange` payload via the setState functional updater inside a microtask.
    if (section1HasUnsaved) dropdown1Ref.current?.commitPending();
    if (section2HasUnsaved) dropdown2Ref.current?.commitPending();

    setIsUnsavedConfirmOpen(false);

    // Defer so the state updates from `commitPending`'s `onChange` land before we read them.
    setTimeout(() => {
      setSection1((s1) => {
        setSection2((s2) => {
          void performSave(s1, s2);
          return s2;
        });
        return s1;
      });
    }, 0);
  };

  const handleCancelUnsavedSave = () => {
    setIsUnsavedConfirmOpen(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <MuiModal
      open={open}
      onClose={handleClose}
      sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
    >
      <ModalContainer>
        <Stack
          direction="row"
          bgcolor={background.disable}
          height="56px"
          minHeight="56px"
          alignItems="center"
          justifyContent="space-between"
          sx={{ borderTopLeftRadius: "8px", borderTopRightRadius: "8px", px: 3 }}
        >
          <Typography variant="body14Light" color="#0B0C0D">人材ステータスを一括変更</Typography>
          <IconBox onClick={handleClose} sx={{ p: 0 }}>
            <IconClose color={black[80]} fontSize={24} />
          </IconBox>
        </Stack>

        <Box sx={{ flex: 1, overflowY: "auto", padding: "24px 32px", display: "flex", flexDirection: "column", gap: 2 }}>
          {[
            { section: section1, setSection: setSection1, otherSection: section2, dropdownRef: dropdown1Ref, setHasUnsaved: setSection1HasUnsaved },
            { section: section2, setSection: setSection2, otherSection: section1, dropdownRef: dropdown2Ref, setHasUnsaved: setSection2HasUnsaved },
          ].map(({ section, setSection, otherSection, dropdownRef, setHasUnsaved }, idx) => (
            <Box
              key={idx}
              sx={{ border: `1px solid ${divider.middle}`, borderRadius: "8px", padding: "24px", display: "flex", flexDirection: "column", gap: 2 }}
            >
              <Stack gap={0.5}>
                <Typography sx={{ fontSize: "12px", color: text.secondary }}>ステータス</Typography>
                <Select
                  nonControl
                  options={statusOptionsForSection(otherSection.status)}
                  value={section.status === null ? "__none__" : section.status}
                  onChange={(value) => setSection((prev) => ({ ...prev, status: value === "" || value === "__none__" ? null : Number(value) }))}
                  placeholder="選択してください"
                  clearable={false}
                />
              </Stack>
              <Stack gap={0.5}>
                <Typography sx={{ fontSize: "12px", color: text.secondary }}>マッチング人材</Typography>
                <TalentSelectDropdown
                  ref={dropdownRef}
                  talents={talentOptionsForSection(otherSection.talentIds)}
                  selectedIds={section.talentIds}
                  onChange={(ids) => setSection((prev) => ({ ...prev, talentIds: ids }))}
                  onUnsavedChange={setHasUnsaved}
                  placeholder="選択してください"
                />
              </Stack>
            </Box>
          ))}
        </Box>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ borderTop: `1px solid ${divider.high}`, padding: "16px 32px", borderBottomLeftRadius: "8px", borderBottomRightRadius: "8px" }}
        >
          <Button size="sm" variant="ghost" onClick={handleClose} sx={{ minWidth: "100px" }}>
            キャンセル
          </Button>
          <Button size="sm" variant="primary" onClick={handleSaveClick} disabled={!canSave} isLoading={isSaving} startIcon={<IconSave />} sx={{ minWidth: "88px" }}>
            保存
          </Button>
        </Stack>

        <ConfirmSaveModal
          open={isUnsavedConfirmOpen}
          onClose={handleCancelUnsavedSave}
          onOk={handleConfirmUnsavedSave}
          enableFooter
        >
          <Typography>選択内容が保存されていませんが、閉じてもよろしいですか？</Typography>
        </ConfirmSaveModal>
      </ModalContainer>
    </MuiModal>
  );
};

const ModalContainer = styled(Box)`
  width: 1100px;
  height: 569px;
  max-width: 95vw;
  max-height: 90vh;
  background-color: ${background.white};
  box-shadow: 0px 8px 24px 0px #00000026;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  outline: none;
  &:focus {
    outline: none;
  }
`;
